<template>
  <define-navigation>
    <pf-button
      v-if="!previousDisabled && wizard.isFirstIteration"
      class="pf-v6-u-display-none pf-v6-u-display-flex-on-sm"
      variant="link"
      @click="wizard.resetWizard(false)"
      ouia-id="wizard-form-actions-reset"
      :small="!wizard.modal"
      :style="{
        'margin-inline-start': wizard.modal ? 'var(--pf-v6-l-flex--spacer)' : undefined,
        order: wizard.modal ? '1' : undefined,
      }"
    >
      {{ $t('Reset') }}
    </pf-button>

    <pf-button v-if="!previousDisabled" variant="tertiary" @click="wizard.previousStep" ouia-id="wizard-form-actions-previous" :small="!wizard.modal">
      <template #icon>
        <angle-left-icon />
      </template>

      {{ $t('Indietro') }}
    </pf-button>

    <pf-button
      type="submit"
      :form="formId"
      variant="primary"
      :aria-disabled="sending || !valid"
      :inoperable-events="[]"
      :loading="sending"
      :disabled="sending"
      ouia-id="wizard-form-actions-next"
      :small="!wizard.modal"
    >
      {{ $t('Avanti') }}
      <template #icon>
        <slot name="next-icon">
          <angle-right-icon />
        </slot>
      </template>
    </pf-button>
  </define-navigation>

  <x-form
    ref="xFormRef"
    :id="formId"
    v-bind="$attrs"
    v-model:sending="sending"
    @submit="wizard.nextStep"
    submit-text=""
    @check-validity="valid = $event"
    :class="{ 'pf-v6-u-display-none': hidden }"
  >
    <template v-if="!hidden && wizard.modal">
      <teleport v-if="wizard.modalBoxTitle" :to="wizard.modalBoxTitle">
        {{ title }}
      </teleport>

      <teleport v-if="wizard.modalFooter && !confirm && !thankyou" :to="toValue(wizard.modalFooter)?.$el">
        <reuse-navigation />
      </teleport>
    </template>

    <pf-flex v-else justify-content="space-between" class="sticky">
      <pf-flex-item v-if="title" align-self="flex-start">
        <pf-title h="2">
          <slot name="icon" />
          {{ title }}
        </pf-title>
      </pf-flex-item>

      <pf-flex-item align-self="flex-end" v-if="!confirm && !thankyou">
        <pf-action-group>
          <pf-button v-if="!wizard.isFirstIteration" @click="showCart = true" small>
            <template #icon>
              <cart-shopping-icon :title="$t('Prodotti già inseriti nell\'offerta')" />
            </template>
          </pf-button>

          <reuse-navigation v-if="!wizard.modal" />
        </pf-action-group>
      </pf-flex-item>

      <pf-flex-item align-self="flex-end" v-else-if="!thankyou && !wizard.modal">
        <pf-button
          variant="secondary"
          :class="{ disabled: wizard.sending }"
          @click="wizard.otherIteration()"
          data-ouia-component-id="wizard-action-continue"
          small
        >
          <template #icon>
            <cart-plus-icon />
          </template>
          {{ $t("Inserisci altri prodotti nell'offerta") }}
        </pf-button>
      </pf-flex-item>
    </pf-flex>

    <slot />

    <pf-modal
      v-if="!wizard.isFirstIteration && wizard.offer"
      :open="showCart"
      :title="$t('Prodotti già inseriti nell\'offerta')"
      variant="medium"
      @update:open="$event ? undefined : (showCart = false)"
    >
      <offer-detail v-if="wizard.offer.prodotti.length" :offerta="wizard.offer" hide-totals @remove-products="wizard.removeProducts($event)" />
      <pf-empty-state v-else>
        <pf-empty-state-header :title="$t('Nessun prodotto in offerta')" heading-level="h4" />
      </pf-empty-state>

      <pf-flex justify-content="flex-end">
        <pf-flex-item shrink>
          <wizard-question :title="$t('Totale')">
            <div v-if="wizard.freePeriod">
              <strong class="free-label">{{ $t('I primi {0} giorni sono gratuiti', [30]) }}</strong>
            </div>

            <pf-description-list v-if="wizard.offer.prodotti.length" horizontal class="payment-totals" term-width="30ch" style="max-width: 80ch">
              <offer-total
                :totali="wizard.offer.totali"
                :vat="wizard.customer === undefined || wizard.customer.vat > 0"
                durata="Annuale"
                :label="$t('Totale ricorrente annuale:')"
                big
                :currency="wizard.customer?.currency ?? 'EUR'"
              />
              <offer-total
                :totali="wizard.offer.totali"
                :vat="wizard.customer === undefined || wizard.customer.vat > 0"
                durata="Mensile"
                :label="$t('Totale ricorrente mensile:')"
                big
                :currency="wizard.customer?.currency ?? 'EUR'"
              />
              <offer-total
                :totali="wizard.offer.totali"
                :vat="wizard.customer === undefined || wizard.customer.vat > 0"
                durata="Una tantum"
                :label="$t('Totale una tantum:')"
                big
                :currency="wizard.customer?.currency ?? 'EUR'"
              />
            </pf-description-list>
          </wizard-question>
        </pf-flex-item>
      </pf-flex>
    </pf-modal>
  </x-form>
</template>

<style lang="scss">
.pf-v6-c-form__group.pf-m-action {
  margin: 0;
}
.sticky {
  position: sticky;
  top: 0;
  z-index: 150;
  background-color: var(--pf-t--global--background--color--primary--default);
  padding: var(--pf-t--global--spacer--sm) 0;
}
</style>

<script lang="ts" setup>
import { $t as _t } from '@/i18n';
import { nextTick, ref, toValue, useId, useTemplateRef, watch, computed } from 'vue';
import { useWizard } from '@/wizard';
import AngleLeftIcon from '@vue-patternfly/icons/angle-left-icon';
import AngleRightIcon from '@vue-patternfly/icons/angle-right-icon';
import CartShoppingIcon from '@vue-patternfly/icons/cart-shopping-icon';
import CartPlusIcon from '@vue-patternfly/icons/cart-plus-icon';
import XForm from '../x-form.vue';
import type { ComponentExposed } from 'vue-component-type-helpers';
import WizardQuestion from './questions/wizard-question.vue';
import OfferDetail from '@/components/offer-detail.vue';
import OfferTotal from '@/components/offer-total.vue';
import { createReusableTemplate } from '@vueuse/core';

defineOptions({
  inheritAttrs: false,
});

const wizard = useWizard();

const props = defineProps<{
  title?: string;
  hidden?: boolean;
  confirm?: boolean;
  thankyou?: boolean;
}>();

const formId = useId();
const xForm = useTemplateRef<ComponentExposed<typeof XForm>>('xFormRef');
const sending = ref(false);
const valid = ref(false);
const showCart = ref(false);
const [DefineNavigation, ReuseNavigation] = createReusableTemplate();

watch(
  () => props.hidden,
  () => {
    if (!props.hidden) {
      nextTick(() => xForm.value?.fieldset?.checkValidity());
    }
  },
);

const previousDisabled = computed(() => wizard.currentStep === wizard.initialStep);
</script>
